import type { SxProps, TypographyProps } from '@mui/material';
import { Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

const ElementResizeEventName = 'elementResize';
let globalResizeObserver: ResizeObserver | null = null;

const getResizeObserver = () => {
    if (!globalResizeObserver) {
        globalResizeObserver = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                entry.target.dispatchEvent(new CustomEvent(ElementResizeEventName));
            });
        });
    }
    return globalResizeObserver;
};

const TypographyStyle: SxProps = {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

export const EllipsisTypography = ({ children, ...props }: TypographyProps) => {
    const textRef = useRef<HTMLElement>(null);
    const [isOverflow, setIsOverflow] = useState(false);

    const checkOverflow = useCallback(() => {
        const element = textRef.current;
        if (!element) {
            return;
        }

        requestAnimationFrame(() => {
            const isOverflowing = element.scrollWidth > element.clientWidth;
            setIsOverflow(isOverflowing);
        });
    }, []);

    useEffect(() => {
        const element = textRef.current;
        if (!element) {
            return () => {};
        }

        // initial check
        checkOverflow();

        const observer = getResizeObserver();
        observer.observe(element);

        const handleResize = () => {
            checkOverflow();
        };
        element.addEventListener(ElementResizeEventName, handleResize);

        return () => {
            observer.unobserve(element);
            element.removeEventListener(ElementResizeEventName, handleResize);
        };
    }, [checkOverflow]);

    return (
        <Tooltip title={children} disableHoverListener={!isOverflow}>
            <Typography
                className="ellipsis-typography"
                ref={textRef}
                {...props}
                sx={TypographyStyle}
            >
                {children}
            </Typography>
        </Tooltip>
    );
};
